import { StoreStaffApi } from "@unit/apis";
import { isDateBefore } from "@unit/utility";
import {
  signInWithEmailAndPassword,
  signOut,
  EmailAuthProvider,
  updatePassword,
  reauthenticateWithCredential,
  MultiFactorResolver,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
} from "firebase/auth";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { useCallback } from "react";


import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import {
  LOCAL_STORAGE_ACCESS_TOKEN_EXPIRES_AT_KEY,
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  STAFF_STORE_LIST_MONTHS_OFFSET,
} from "@/global-state/app-config";
import { API_URL } from "@/global-state/firebase-settings";
import { firebaseGetAuth } from "@/global-state/firebase-settings";
import { authUserAtom, idTokenAtom, loadingAtom, selectStoreAtom } from "@/global-state/jotai-atom";
import { homePath } from "@/global-state/store-app-path";

import { useMultiFactor } from "./use-multi-factor";
import { useResetAuth } from "./use-reset-auth";

export const useAuthentication = () => {
  const router = useRouter();
  const { setAppSnackbar } = useAppSnackbar();
  const { resetAuth } = useResetAuth();
  const { sendUserMFA } = useMultiFactor();

  const [, setLoading] = useAtom(loadingAtom);
  const [idToken, setIdToken] = useAtom(idTokenAtom);
  const [, setAuthUser] = useAtom(authUserAtom);
  const [, setSelectStoreAtom] = useAtom(selectStoreAtom);

  const syncIdToken = useCallback(async () => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
    const expiresAtStr = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_EXPIRES_AT_KEY);

    if (accessToken && expiresAtStr) {
      const now = new Date().getTime();
      const expiresAt = parseInt(expiresAtStr);
      if (now < expiresAt) {
        setIdToken(accessToken);
        return accessToken;
      }
    }

    return new Promise<string | null>((resolve) => {
      firebaseGetAuth.onAuthStateChanged(async (user) => {
        const accessToken = (await user?.getIdToken(true)) || undefined;
        if (!accessToken) {
          await resetAuth();
          resolve(null);
          return;
        }

        // 有効期限は取得したタイミングから1時間以内だが、アプリ上では余裕を持って、30分以内とする
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_EXPIRES_AT_KEY, `${new Date().getTime() + 30 * 60 * 1000}`);
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, accessToken);

        await setIdToken(accessToken);
        resolve(accessToken);
      });
    });
  }, []);

  const syncAuthUser = useCallback(async () => {
    setLoading(true);
    try {
      const checkApi = new StoreStaffApi(API_URL, idToken);
      const member = await checkApi.getStaffProfile();
      await setAuthUser(member.object);
    } catch (e: any) {
      console.error(e?.response?.data?.message || e?.response?.data?.devMessage);
      await resetAuth();
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  const authReFetchMember = useCallback(async () => {
    setLoading(true);
    firebaseGetAuth.onAuthStateChanged(async (user) => {
      const accessToken = (await user?.getIdToken(true)) || undefined;
      if (!accessToken) return resetAuth();
      await setIdToken(accessToken);
      const checkApi = new StoreStaffApi(accessToken);
      const member = await checkApi.getStaffProfile();
      if (!member) return resetAuth();
      await setAuthUser(member.object);
      const selectable = member.object.stores.filter(
        (s) => !isDateBefore(s.contractEndDate, undefined, STAFF_STORE_LIST_MONTHS_OFFSET),
      );
      if (!selectable.length) return resetAuth();
      await setSelectStoreAtom(selectable[0]);
    });
    setLoading(false);
  }, []);

  const authFinishingLogin = async (accessToken: string, routeToHome?: boolean) => {
    try {
      await setIdToken(accessToken);

      const checkApi = new StoreStaffApi(API_URL, accessToken);
      const member = await checkApi.getStaffProfile();
      const selectable = member.object.stores.filter(
        (s) => !isDateBefore(s.contractEndDate, undefined, STAFF_STORE_LIST_MONTHS_OFFSET),
      );
      const initStore = selectable[0];
      if (!initStore) {
        throw new Error("どのストアにも所属していないユーザーなのでログインできません");
      }
      await setAuthUser(member.object);
      await setSelectStoreAtom(initStore);
      if (routeToHome) {
        await router.push(homePath);
      }
      setAppSnackbar(`${member.object.name}でログインしました。`, { success: true });
    } catch (e: any) {
      setAppSnackbar(e.message || "ストア管理者ではありません。", { error: true });
      await resetAuth();
    } finally {
      setLoading(false);
    }
  };

  // Email&Passwordでのログイン
  const authLoginWithEmail = useCallback(
    async (
      email: string,
      password: string,
      onMultiAuth: (v: { resolver: MultiFactorResolver; verificationId: string }) => void,
    ) => {
      setLoading(true);

      const result = await signInWithEmailAndPassword(firebaseGetAuth, email, password)
        .then(async (credential) => (await credential.user?.getIdToken(true)) || undefined)
        .catch(async (error) => {
          let errorMessage = "";
          switch (error.code) {
            case "auth/multi-factor-auth-required":
              const response = await sendUserMFA(error);
              if (response) {
                onMultiAuth(response);
                return false;
              }

              errorMessage = "2段階認証が必要です。認証コードを入力してください。";
              break;
            case "auth/user-not-found":
              errorMessage = "ストア管理者で登録がありませんでした";
              break;
            case "auth/wrong-password":
              errorMessage = "パスワードが一致しませんでした。";
              break;
            default:
              errorMessage = error?.response?.data?.detail || error?.message || error;
          }
          setAppSnackbar(errorMessage, { error: true });
          return undefined;
        });

      if (typeof result === "boolean") {
        setLoading(false);
        return;
      }

      if (!result) {
        return await resetAuth();
      }

      await authFinishingLogin(result, true);
    },
    [],
  );

  // ログアウト
  const authLogout = useCallback(async () => {
    setLoading(true);
    signOut(firebaseGetAuth)
      .then(async () => {
        setAppSnackbar("ログアウトしました。", { info: true });
        await resetAuth();
      })
      .catch((error) => {
        setAppSnackbar(error?.message || "error", { error: true });
      })
      .finally(() => setLoading(false));
  }, []);

  const authResetPassword = useCallback(async (prevPassword: string, newPassword: string) => {
    setLoading(true);
    const user = firebaseGetAuth.currentUser;
    const providerData = user?.providerData.length ? user?.providerData[0].providerId : "";
    if (!user || !user.email || !providerData) return await resetAuth();
    if (providerData === "google.com") {
      return setAppSnackbar("Googleでログインしているためパスワードの変更はできません。", { success: true });
    } else if (providerData === "facebook.com") {
      return setAppSnackbar("Facebookでログインしているためパスワードの変更はできません。", { success: true });
    }
    const credential = await EmailAuthProvider.credential(user.email, prevPassword);
    if (!credential) return resetAuth();
    await reauthenticateWithCredential(user, credential)
      .then(async (cred) => {
        await updatePassword(cred.user, newPassword).then(() => {
          setAppSnackbar("パスワードの更新に成功しました。", { success: true });
        });
      })
      .catch((error) => {
        console.log(error);
        setAppSnackbar("パスワードを更新できませんでした。", { error: true });
      })
      .finally(() => setLoading(false));
  }, []);

  const reauthenticateWithPassword = useCallback(
    async (
      password: string,
      onMultiAuth: (v: { resolver: MultiFactorResolver; verificationId: string }) => void,
    ): Promise<boolean | void> => {
      setLoading(true);
      const user = firebaseGetAuth.currentUser;
      if (!user || !user.email) return await resetAuth();

      const credential = EmailAuthProvider.credential(user.email, password);
      if (!credential) return resetAuth();
      return await reauthenticateWithCredential(user, credential)
        .then(() => {
          setAppSnackbar("再認証に成功しました。引き続き操作を実行できます。", { success: true });
          return true;
        })
        .catch(async (error) => {
          if (error.code === "auth/multi-factor-auth-required") {
            const response = await sendUserMFA(error);
            if (response) {
              onMultiAuth(response);
              return false;
            }
          }
          setAppSnackbar("再認証に失敗しました。パスワードが正しいか確認してください。", { error: true });
          return false;
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  const verifyUserMFA = useCallback(
    async (resolver: MultiFactorResolver, verificationId: string, code: string, routeToHome?: boolean) => {
      try {
        setLoading(true);
        const cred = PhoneAuthProvider.credential(verificationId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(cred);
        const accessToken = await (await resolver.resolveSignIn(assertion)).user.getIdToken(true);

        if (!accessToken) {
          return await resetAuth();
        }

        await authFinishingLogin(accessToken, routeToHome);
        return true;
      } catch (e: any) {
        setAppSnackbar("エラーが発生しました。再度やり直してください。", { error: true });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    syncIdToken,
    syncAuthUser,
    authReFetchMember,
    authLoginWithEmail,
    authLogout,
    authResetPassword,
    reauthenticateWithPassword,
    verifyUserMFA,
  };
};
